import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { supabase } from "@core/lib/supabase";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Alert, AlertDescription } from "@core/components/ui/alert";

type ViewState = "idle" | "submitting" | "sent" | "error";

/**
 * Page publique "Mot de passe oublié".
 * Demande l'email, invoque l'edge function `send-password-reset` qui envoie
 * un lien brandé DooQuest pointant vers /auth/reset-password.
 *
 * IMPORTANT : on affiche toujours un message générique (succès) que l'email
 * existe ou non, pour éviter toute attaque par énumération.
 */
export default function ForgotPasswordPage() {
  const { t } = useTranslation("forms");
  const [email, setEmail] = useState("");
  const [state, setState] = useState<ViewState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || state === "submitting") return;

    setState("submitting");
    setErrorMessage(null);

    try {
      const { error } = await supabase.functions.invoke("send-password-reset", {
        body: {
          email,
          redirectTo: `${window.location.origin}/auth/reset-password`,
        },
      });

      // Anti-énumération : quel que soit le retour (email existe ou non),
      // on affiche la même confirmation. On ne surface une erreur qu'en cas
      // de panne réseau / edge function complètement down.
      if (error && (error as any).status !== 404) {
        // On log l'erreur en console mais on ne la montre pas à l'utilisateur
        // sauf panne manifeste.
        console.warn(
          "send-password-reset error (silencing for anti-enum):",
          error,
        );
      }

      setState("sent");
    } catch (err) {
      console.error("ForgotPasswordPage unexpected error:", err);
      setState("error");
      setErrorMessage(t("auth.forgot.networkError"));
    }
  }

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">
            {t("auth.forgot.title")}
          </CardTitle>
          <CardDescription className="text-center">
            {t("auth.forgot.description")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {state === "sent" ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                {t("auth.forgot.sentPrefix", { email })}
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t("common.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder={t("auth.forgot.emailPlaceholder")}
                    autoComplete="email"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={state === "submitting"}
                  />
                </div>
              </div>

              {state === "error" && errorMessage && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={state === "submitting" || !email}
              >
                {state === "submitting"
                  ? t("auth.forgot.submitting")
                  : t("auth.forgot.submit")}
              </Button>
            </form>
          )}

          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              {t("auth.backToLogin")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
